/**
 * TestModeWipeTab — Wave 6.3 SuperAdmin nuke-the-test-data surface.
 *
 * Single button "Wipe all test-mode records". Requires typing
 * `DELETE TEST DATA` to confirm. No SQL audit history written (per Bill's
 * "no recording" for test-mode rule).
 *
 * Counts shown post-purge for SuperAdmin's session only — aggregate-only,
 * no per-officer/branch breakdown.
 */

import { useState } from 'react';
import { ApiError } from '@retail-ops/api-client';
import { Banner, Button } from '@/components/primitives';
import { promptBypassKey } from '@/modules/Admin/editors/_shared/CrudTable';
import { useRecordsApi } from '../hooks/useRecordsApi';
import type { RecordsTestModeWipeResponse } from '@retail-ops/shared-types';

const CONFIRM_PHRASE = 'DELETE TEST DATA';

export function TestModeWipeTab(): JSX.Element {
  const { client, makeSignal } = useRecordsApi();
  const [phrase, setPhrase] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RecordsTestModeWipeResponse | null>(null);

  const phraseValid = phrase === CONFIRM_PHRASE;

  async function handleWipe(): Promise<void> {
    if (!phraseValid || submitting) return;
    const bypass = promptBypassKey();
    if (!bypass) {
      setError('A bypass key is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await client.retail.superAdmin.records.testModeWipe(
        bypass,
        { confirmation: CONFIRM_PHRASE },
        { signal: makeSignal() },
      );
      if (!res.success) {
        setError(res.error ?? 'Test-mode wipe returned success=false.');
        return;
      }
      setResult(res);
      setPhrase('');
    } catch (e: unknown) {
      if (ApiError.isApiError(e)) setError(e.message);
      else if (e instanceof Error) setError(e.message);
      else setError('Unexpected error during test-mode wipe.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', maxWidth: 720 }}>
      <Banner tone="warn" title="Test-mode wipe is permanent and unaudited">
        Removes every record where <code>isTestMode = true</code> across Carts, CartProduct,
        and MaintActions. By design no PurgeHistory rows are written for these — they were
        never real customer transactions to begin with.
      </Banner>

      <div
        style={{
          padding: 'var(--sp-7)',
          background: 'var(--surface-card)',
          border: '1px solid var(--surface-border)',
          borderRadius: 'var(--r-md)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
        }}
      >
        <div style={{ fontSize: 14, color: 'var(--ink-800)', lineHeight: 1.5 }}>
          To confirm, type <strong>{CONFIRM_PHRASE}</strong> exactly into the field below.
          This is the only acceptable confirmation token; any other text is rejected.
        </div>
        <input
          aria-label="Confirmation phrase"
          title="Confirmation phrase"
          type="text"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          placeholder={CONFIRM_PHRASE}
          style={{
            padding: 'var(--sp-4)',
            fontSize: 14,
            fontFamily: 'monospace',
            border: '1px solid var(--ink-300)',
            borderRadius: 'var(--r-md)',
            letterSpacing: '0.04em',
          }}
        />
        <div>
          <Button variant="danger" disabled={!phraseValid || submitting} onClick={() => void handleWipe()}>
            {submitting ? 'Wiping…' : `Wipe all test-mode records`}
          </Button>
        </div>
      </div>

      {error && (
        <Banner tone="err" title="Wipe failed">
          {error}
        </Banner>
      )}

      {result && (
        <Banner tone="ok" title="Wipe complete">
          Removed{' '}
          <strong>{result.cartsWiped ?? 0}</strong> cart
          {(result.cartsWiped ?? 0) === 1 ? '' : 's'},{' '}
          <strong>{result.cartProductsWiped ?? 0}</strong> per-product row
          {(result.cartProductsWiped ?? 0) === 1 ? '' : 's'}, and{' '}
          <strong>{result.maintActionsWiped ?? 0}</strong> maintenance action
          {(result.maintActionsWiped ?? 0) === 1 ? '' : 's'}.
        </Banner>
      )}
    </div>
  );
}
